import { Slider } from '@talentmesh/ui/components/slider';
import { TypographyP } from '@talentmesh/ui/components/typography';
import { cn } from '@talentmesh/ui/lib/utils';
import { useEffect, useState } from 'react';
import { useCandidatesResultsContext } from '../../Contexts/CandidateResultsContext';

import CTMonitoringScreenshotsStub from './CTMonitoringScreenshotsStub';
import CTMonitoringWebcamStub from './CTMonitoringWebcamStub';
import Snapshot from './Snapshot';
import SnapshotMetadata from './SnapshotMetadata';
import { SnapshotsSliceDTO } from '@/DTO/Snapshots/SnapshotsSliceDTO';
import { useSnapshotsClient } from '@/Hooks/ClientHooks';
import { TestTypes } from '@/Models/Configuration';
import UIStrings from '@/Utils/UIStrings';

export interface CTMonitoringBodyProps {
    isOpened: boolean;
    testType: TestTypes;
    isWebcamEnabled: boolean;
    isScreenSnapshotsEnabled: boolean;
}

function CTMonitoringBody({ isOpened, testType, isWebcamEnabled, isScreenSnapshotsEnabled }: CTMonitoringBodyProps) {
    const { results } = useCandidatesResultsContext();
    const [items, setItems] = useState<Array<SnapshotsSliceDTO>>([]);
    const [current, setCurrent] = useState<number>(0);
    const client = useSnapshotsClient();

    useEffect(() => {
        setCurrent((c) => {
            if (items.length === 0) {
                return 0;
            }
            return Math.min(c, items.length - 1);
        });
    }, [items.length]);

    useEffect(() => {
        if (!items || items.length === 0) {
            const doGetSnapshots = async () => {
                const snapshots = await client.getSnapshotsAsync(results.assessmentId, results.candidateId, testType);
                setItems(snapshots.data);
            };
            doGetSnapshots();
        }
    }, []);

    const maxIndex = items.length > 0 ? items.length - 1 : 0;

    return (
        <div
            className={cn(
                'border-border bg-card flex flex-col gap-4 rounded-b-lg border border-t-0 p-4',
                !isOpened && 'hidden',
            )}
        >
            <TypographyP className="text-sm">{UIStrings.TestMonitoringExplanation}</TypographyP>
            <div className="bg-muted flex flex-row gap-4 rounded-md p-4">
                <div className="w-3/5 min-w-0">
                    {items[current]?.screenshotUrl ? (
                        <Snapshot url={items[current]?.screenshotUrl} title={UIStrings.ScreenSnapshots} />
                    ) : (
                        <CTMonitoringScreenshotsStub isScreenSnapshotsEnabled={isScreenSnapshotsEnabled} />
                    )}
                </div>
                <div className="flex w-2/5 min-w-0 flex-col gap-4">
                    {isWebcamEnabled && items[current]?.webcamPhotoUrl ? (
                        <Snapshot url={items[current]?.webcamPhotoUrl} title={UIStrings.Webcam} />
                    ) : (
                        <CTMonitoringWebcamStub />
                    )}
                    {items[current] ? (
                        <SnapshotMetadata current={current + 1} total={items.length} date={items[current].createdAt} />
                    ) : null}
                </div>
            </div>
            {items.length > 0 ? (
                <Slider
                    className="w-full"
                    value={[current]}
                    min={0}
                    max={maxIndex}
                    step={1}
                    onValueChange={(next) => {
                        const nextValue = next[0];
                        if (typeof nextValue === 'number') {
                            setCurrent(nextValue);
                        }
                    }}
                />
            ) : null}
        </div>
    );
}

export default CTMonitoringBody;
